import { Navigate, Routes as ReactRoutes, Route } from 'react-router-dom';
import { observer } from 'mobx-react';

import { PlayUserType } from '@genially/p2p-lib';
import { useEffectOnce } from '@genially/ui/src/hooks/useEffectOnce';

import { HostClientProvider } from '../../../../../host/application/pages/HostGamePage/components/HostClientProvider/HostClientProvider';
import { HostGamePage } from '../../../../../host/application/pages/HostGamePage/HostGamePage';
import { PlayerClientProvider } from '../../../../../player/application/pages/PlayerGamePage/components/PlayerClientProvider/PlayerClientProvider';
import { PlayerGamePage } from '../../../../../player/application/pages/PlayerGamePage/PlayerGamePage';
import { PlayerKickedPage } from '../../../../../player/application/pages/PlayerKickedPage/PlayerKickedPage';
import { PlayerSessionSelectionPage } from '../../../../../player/application/pages/PlayerSessionSelectionPage/PlayerSessionSelectionPage';
import { PlayersLimitReachedPage } from '../../../../../player/application/pages/PlayersLimitReachedPage/PlayersLimitReachedPage';
import { useInitSnowplow } from '../../../../../shared/application/hooks/useSnowplow';
import { GeniallyDraftErrorPage } from '../../../../../shared/application/pages/GeniallyDraftErrorPage/GeniallyDraftErrorPage';
import { GeniallyRestrictedErrorPage } from '../../../../../shared/application/pages/GeniallyRestrictedErrorPage/GeniallyRestrictedErrorPage';
import { GeniallyWithPasswordErrorPage } from '../../../../../shared/application/pages/GeniallyWithPasswordErrorPage/GeniallyWithPasswordErrorPage';
import { NoGeniallyAccessErrorPage } from '../../../../../shared/application/pages/NoGeniallyAccessErrorPage/NoGeniallyAccessErrorPage';
import { NotFoundErrorPage } from '../../../../../shared/application/pages/NotFoundErrorPage/NotFoundErrorPage';
import { UnknownErrorPage } from '../../../../../shared/application/pages/UnknownErrorPage/UnknownErrorPage';
import { RouteNames } from '../../../../domain/RouteNames';
import { PlayersLimitsProvider } from '../components/PlayersLimitsProvider/PlayersLimitsProvider.context';
import { PlayFeatureFlagProvider } from '../components/PlayFeatureFlagProvider/PlayFeatureFlagProvider';
import { PlayerJoinRoute } from './components/PlayerJoinRoute/PlayerJoinRoute';
import { CheckHostGeniallyAccessGuard } from './guards/CheckHostGeniallyAccessGuard';
import { CheckHostParamsGuard } from './guards/CheckHostParamsGuard';
import { CheckIsGeniallyCompatibleGuard } from './guards/CheckIsGeniallyCompatibleGuard';
import { CheckMonthlyPlayersLimitNotReached } from './guards/CheckMonthlyPlayersLimitNotReached';
import { CheckSessionIdGuard } from './guards/CheckSessionIdGuard';
import { TrackPageView } from './hocs/TrackPageView/TrackPageView';
import { useLogRocketService } from './hooks/useLogRocketService';
import { CommonLayout } from './layouts/CommonLayout/CommonLayout';
import { PlayerConfigWizardLayout } from './layouts/PlayerConfigWizardLayout/PlayerConfigWizardLayout';

export const Routes = observer(() => {
  const { init } = useLogRocketService();

  useInitSnowplow();

  useEffectOnce(() => {
    init();
  });

  return (
    <ReactRoutes>
      <Route path="/" element={<CommonLayout />}>
        {[RouteNames.Host, RouteNames.HostWithoutSessionId].map(routePath => (
          <Route
            key={routePath}
            path={routePath}
            element={
              <CheckHostParamsGuard>
                {({ geniallyId, sessionId, teamId }) => (
                  <CheckIsGeniallyCompatibleGuard
                    geniallyId={geniallyId}
                    userType={PlayUserType.Host}
                  >
                    <CheckHostGeniallyAccessGuard>
                      {({ geniallyUserId }) => (
                        <PlayersLimitsProvider teamId={teamId}>
                          <CheckMonthlyPlayersLimitNotReached>
                            <PlayFeatureFlagProvider teamId={teamId}>
                              <HostClientProvider
                                params={{ sessionId, teamId, geniallyId, geniallyUserId }}
                              >
                                <HostGamePage />
                              </HostClientProvider>
                            </PlayFeatureFlagProvider>
                          </CheckMonthlyPlayersLimitNotReached>
                        </PlayersLimitsProvider>
                      )}
                    </CheckHostGeniallyAccessGuard>
                  </CheckIsGeniallyCompatibleGuard>
                )}
              </CheckHostParamsGuard>
            }
          />
        ))}

        <Route
          path={RouteNames.Player}
          element={
            <CheckSessionIdGuard>
              {({ geniallyId, sessionId, username }) => (
                <CheckIsGeniallyCompatibleGuard
                  geniallyId={geniallyId}
                  userType={PlayUserType.Player}
                >
                  <PlayerClientProvider params={{ sessionId, geniallyId, username }}>
                    <TrackPageView>
                      <PlayerGamePage />
                    </TrackPageView>
                  </PlayerClientProvider>
                </CheckIsGeniallyCompatibleGuard>
              )}
            </CheckSessionIdGuard>
          }
        />

        <Route path={RouteNames.PlayerUsernameSelection} element={<PlayerJoinRoute />} />

        <Route element={<PlayerConfigWizardLayout />}>
          <Route
            path={RouteNames.PlayerSessionFinder}
            element={
              <TrackPageView>
                <PlayerSessionSelectionPage />
              </TrackPageView>
            }
          />
        </Route>

        <Route
          path={RouteNames.PlayerKicked}
          element={
            <TrackPageView>
              <PlayerKickedPage />
            </TrackPageView>
          }
        />
        <Route
          path={RouteNames.PlayersLimitReached}
          element={
            <TrackPageView>
              <PlayersLimitReachedPage />
            </TrackPageView>
          }
        />

        <Route
          path={RouteNames.NotFound}
          element={
            <TrackPageView>
              <NotFoundErrorPage />
            </TrackPageView>
          }
        />
        <Route
          path={RouteNames.UnknownError}
          element={
            <TrackPageView>
              <UnknownErrorPage />
            </TrackPageView>
          }
        />
        <Route
          path={RouteNames.NoGeniallyAccess}
          element={
            <TrackPageView>
              <NoGeniallyAccessErrorPage />
            </TrackPageView>
          }
        />
        <Route
          path={RouteNames.GeniallyDraft}
          element={
            <TrackPageView>
              <GeniallyDraftErrorPage />
            </TrackPageView>
          }
        />
        <Route
          path={RouteNames.GeniallyWithPassword}
          element={
            <TrackPageView>
              <GeniallyWithPasswordErrorPage />
            </TrackPageView>
          }
        />
        <Route
          path={RouteNames.GeniallyRestricted}
          element={
            <TrackPageView>
              <GeniallyRestrictedErrorPage />
            </TrackPageView>
          }
        />

        <Route
          path="/"
          element={<Navigate to={RouteNames.PlayerSessionFinder} replace />}
        />
        <Route
          path="*"
          element={
            <TrackPageView>
              <NotFoundErrorPage />
            </TrackPageView>
          }
        />
      </Route>
    </ReactRoutes>
  );
});
